import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { Button, Stack, MenuItem, TextField, Typography } from '@emburse/embark-core';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import ForteApplicationFormRow from '../Common/ForteApplicationFormRow';
import { logger } from '../../../util/logger';
import { ForteApplicationContext } from '../Common/ForteApplicationProvider';
import { ApplicantOrganizationBusinessOwnershipType, ApplicantOrganizationBusinessType } from '../../../api/forteApplication';
import { FormAutocomplete, FormSelect, FormTextField } from '../../../components/FormComponents';
function BusinessInformationForm({ afterFormSave, saveButtonText, backButtonText, backButtonCallback }) {
    useTranslation();
    const { currentApplication, saveApplication, error, loading, states, businessCategoriesAndTypes } = useContext(ForteApplicationContext);
    const formSchema = useMemo(() => z
        .object({
        dbaName: z
            .string()
            .min(1, { message: $t('DBA Name is required') })
            .max(50, { message: $t('DBA Name must be at most 50 characters long') }),
        legalName: z.string().min(1, { message: $t('Legal Name is required') }),
        businessOwnershipType: z.nativeEnum(ApplicantOrganizationBusinessOwnershipType, {
            message: $t('Business Ownership Type is required')
        }),
        taxIdNumber: z
            .string()
            .min(1, { message: 'Tax ID must not be empty' })
            .max(30, { message: 'Tax ID must be at most 30 characters long' })
            .regex(/^(?:\d{9}|\d{3}-\d{2}-\d{4}|9\d{2}-\d{2}-\d{4}|9\d{8}|\d{2}-\d{7})$/, {
            message: 'Tax ID must be in one of the following formats: SSN (XXXXXXXXX, XXX-XX-XXXX), ' +
                'ITIN (9XXXXXXXX, 9XX-XX-XXXX), EIN (XXXXXXXXX, XX-XXXXXXX)'
        }),
        address: z
            .string()
            .min(1, { message: 'Street Address cannot be empty' })
            .max(50, { message: 'Street Address cannot be longer than 50 characters' })
            .regex(/^(?!.*\b(?:P\.?O\.?|P\.? ?O\.?|Post Office|UPS Store|Mailbox)\b).*$/, {
            message: 'Street Address cannot contain P.O. boxes or mailbox services like the UPS Store'
        }),
        zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, {
            message: $t('Zip Code must be in the format XXXXX or XXXXX-XXXX')
        }),
        city: z.string().min(1, { message: $t('City is required') }),
        state: z.object({
            Code: z.string().min(1, { message: $t('State is required') })
        }),
        customerServiceNumber: z
            .string()
            .min(1, { message: $t('Phone Number is required') })
            .max(12, {
            message: $t('Phone Number must be at most 12 characters long')
        }),
        businessWebsite: z
            .string()
            .min(1, { message: 'Website must not be empty' })
            .max(100, { message: 'Website must be at most 100 characters long' })
            .regex(/^www\.[a-zA-Z0-9-_./]+$/, {
            message: 'Website must be in the format www.yourcompanysite.com'
        }),
        businessCategory: z
            .string()
            .min(1, { message: $t('Business Category is required') }),
        typeOfBusiness: z.nativeEnum(ApplicantOrganizationBusinessType, {
            message: $t('Type of Business is required')
        }),
        averageSaleAmount: z
            .string()
            .min(1, { message: $t('Average Reimbursement Amount is required') })
            .regex(/^\d+(\.\d{1,2})?$/, {
            message: $t('Average Reimbursement Amount must be a valid number')
        }),
        maximumSaleAmount: z
            .string()
            .min(1, { message: $t('Maximum Reimbursement Amount is required') })
            .regex(/^\d+(\.\d{1,2})?$/, {
            message: $t('Maximum Reimbursement Amount must be a valid number')
        }),
        annualSales: z
            .string()
            .min(1, { message: $t('Annual Reimbursement Spend is required') })
            .regex(/^\d+(\.\d{1,2})?$/, {
            message: $t('Annual Reimbursement Spend must be a valid number')
        })
    })
        .superRefine((data, ctx) => {
        const maximumSaleAmount = parseFloat(data.maximumSaleAmount);
        const averageSaleAmount = parseFloat(data.averageSaleAmount);
        const annualSales = parseFloat(data.annualSales);
        if (maximumSaleAmount <= averageSaleAmount) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Maximum Reimbursement Amount must be larger than Average Reimbursement Amount',
                path: ['maximumSaleAmount']
            });
        }
        if (annualSales <= maximumSaleAmount) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Annual Reimbursement Spend must be larger than Maximum Reimbursement Amount',
                path: ['annualSales']
            });
        }
        if (annualSales <= averageSaleAmount) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Annual Reimbursement Spend must be larger than Average Reimbursement Amount',
                path: ['annualSales']
            });
        }
    }), []);
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            dbaName: '',
            legalName: '',
            businessOwnershipType: ApplicantOrganizationBusinessOwnershipType.CCorporation,
            taxIdNumber: '',
            address: '',
            zipCode: '',
            city: '',
            state: { Name: '', Code: '' },
            customerServiceNumber: '',
            businessWebsite: '',
            businessCategory: '',
            typeOfBusiness: ApplicantOrganizationBusinessType.A10700,
            averageSaleAmount: '',
            maximumSaleAmount: '',
            annualSales: ''
        },
        shouldUnregister: false
    });
    useEffect(() => {
        if (!loading && !error && currentApplication) {
            let businessCategory;
            Object.keys(businessCategoriesAndTypes).forEach((category) => {
                const categoryMap = businessCategoriesAndTypes[category];
                if (categoryMap.find((option) => option.Code === currentApplication.applicant_organization.business_type)) {
                    businessCategory = category;
                }
            });
            const formData = {
                legalName: currentApplication.applicant_organization.legal_name || '',
                taxIdNumber: currentApplication.applicant_organization.tax_id_number || '',
                dbaName: currentApplication.applicant_organization.dba_name || '',
                address: currentApplication.applicant_organization.street_address1 || '',
                zipCode: currentApplication.applicant_organization.postal_code || '',
                city: currentApplication.applicant_organization.locality || '',
                state: states.find((option) => option.Code === currentApplication.applicant_organization.region) || states[0],
                customerServiceNumber: currentApplication.applicant_organization.customer_service_phone || '',
                businessWebsite: currentApplication.applicant_organization.website || '',
                businessCategory: businessCategory || '',
                businessOwnershipType: currentApplication.applicant_organization.legal_structure
                    ? currentApplication.applicant_organization
                        .legal_structure
                    : ApplicantOrganizationBusinessOwnershipType.CCorporation,
                typeOfBusiness: currentApplication.applicant_organization.business_type ||
                    ApplicantOrganizationBusinessType.A10700,
                averageSaleAmount: currentApplication.average_transaction_amount.toString(),
                maximumSaleAmount: currentApplication.maximum_transaction_amount?.toString() || '',
                annualSales: currentApplication.annual_volume.toString()
            };
            form.reset(formData);
        }
    }, [loading, currentApplication, form, error, states, businessCategoriesAndTypes]);
    const submitForm = useCallback(async (data) => {
        try {
            if ([
                ApplicantOrganizationBusinessOwnershipType.Government,
                ApplicantOrganizationBusinessOwnershipType.SoleProprietorship,
                ApplicantOrganizationBusinessOwnershipType.PubliclyHeldCorporation
            ].includes(data.businessOwnershipType)) {
                currentApplication.owner_2 = undefined;
                currentApplication.owner_3 = undefined;
                currentApplication.owner_4 = undefined;
            }
            await saveApplication({
                ...currentApplication,
                applicant_organization: {
                    ...currentApplication?.applicant_organization,
                    legal_name: data.legalName,
                    tax_id_number: data.taxIdNumber,
                    dba_name: data.dbaName,
                    street_address1: data.address,
                    postal_code: data.zipCode,
                    locality: data.city,
                    region: data.state.Code,
                    customer_service_phone: data.customerServiceNumber,
                    website: data.businessWebsite,
                    legal_structure: data.businessOwnershipType,
                    business_type: data.typeOfBusiness
                },
                average_transaction_amount: Number(data.averageSaleAmount),
                maximum_transaction_amount: Number(data.maximumSaleAmount),
                annual_volume: Number(data.annualSales)
            });
            afterFormSave();
        }
        catch (e) {
            logger.error(e);
        }
    }, [afterFormSave, currentApplication, saveApplication]);
    return (_jsx(FormProvider, { ...form, children: _jsx("form", { children: _jsxs(Stack, { alignItems: "flex-start", sx: { width: '100%' }, spacing: 3, children: [_jsxs(Stack, { direction: "column", spacing: 0, alignItems: "flex-start", sx: { width: '100%' }, children: [_jsx(Typography, { variant: "h6", sx: { flexGrow: 1 }, children: $t('Business Information') }), _jsx(Typography, { variant: "body2", color: "textSecondary", children: $t(`Enter your organization’s details. This information will be submitted to our payment provider, CSG Forte, as part of your organization’s application for electronic ACH reimbursements and payments.`) })] }), _jsxs(ForteApplicationFormRow, { children: [_jsx(FormTextField, { label: $t('DBA Name (Doing Business As)'), id: "dbaName", name: "dbaName", fullWidth: true, helperText: form.formState.errors.dbaName?.message ||
                                    $t("Name that should appear on your customer's statements.") }), _jsx(FormTextField, { label: $t('Legal Name'), id: "legalName", name: "legalName", fullWidth: true, helperText: form.formState.errors.legalName?.message ||
                                    $t('Legal name of your business - must match Tax ID Number') })] }), _jsxs(ForteApplicationFormRow, { children: [_jsxs(FormSelect, { label: $t('Business Ownership Type'), id: "businessOwnershipType", name: "businessOwnershipType", helperText: form.formState.errors.businessOwnershipType?.message, fullWidth: true, children: [_jsx(MenuItem, { value: ApplicantOrganizationBusinessOwnershipType.CCorporation, children: $t('C Corporation') }), _jsx(MenuItem, { value: ApplicantOrganizationBusinessOwnershipType.SCorporation, children: $t('S Corporation') }), _jsx(MenuItem, { value: ApplicantOrganizationBusinessOwnershipType.SoleProprietorship, children: $t('Sole Proprietorship') }), _jsx(MenuItem, { value: ApplicantOrganizationBusinessOwnershipType.LimitedLiabilityCorporation, children: $t('Limited Liability Corporation') }), _jsx(MenuItem, { value: ApplicantOrganizationBusinessOwnershipType.PartnershipGeneralOrLimited, children: $t('Partnership General Or Limited') }), _jsx(MenuItem, { value: ApplicantOrganizationBusinessOwnershipType.PubliclyHeldCorporation, children: $t('Publicly Held Corporation') }), _jsx(MenuItem, { value: ApplicantOrganizationBusinessOwnershipType.TaxExemptOrNonProfitOrganization, children: $t('Tax Exempt Or Non Profit Organization') }), _jsx(MenuItem, { value: ApplicantOrganizationBusinessOwnershipType.Government, children: $t('Government') })] }), _jsx(FormTextField, { id: "taxIdNumber", name: "taxIdNumber", label: $t('Tax ID Number'), fullWidth: true, helperText: form.formState.errors.taxIdNumber?.message ||
                                    $t('Tax Identification Number for your business') })] }), _jsx(ForteApplicationFormRow, { children: _jsx(FormTextField, { id: "address", name: "address", label: $t('Address'), fullWidth: true, helperText: form.formState.errors.taxIdNumber?.message ||
                                $t('Physical address of your business - No P.O. Boxes or Paid Address (i.e. UPS Stores)') }) }), _jsxs(ForteApplicationFormRow, { bottomSpacer: !(!!form.formState.errors.zipCode ||
                            !!form.formState.errors.city ||
                            !!form.formState.errors.state), children: [_jsx(FormTextField, { id: "city", name: "city", label: $t('City'), fullWidth: true, helperText: form.formState.errors.city?.message }), _jsx(FormAutocomplete, { id: "state", name: "state", fullWidth: true, options: states, getOptionLabel: (option) => option.Name, isOptionEqualToValue: (option, value) => option.Code === value.Code, renderInput: (params) => (_jsx(TextField, { ...params, fullWidth: true, label: $t('State'), error: !!form.formState.errors.state, helperText: form.formState.errors.state?.message })) }), _jsx(FormTextField, { id: "zipCode", name: "zipCode", label: $t('Zip Code'), fullWidth: true, helperText: form.formState.errors.zipCode?.message })] }), _jsxs(ForteApplicationFormRow, { bottomSpacer: !(!!form.formState.errors.customerServiceNumber ||
                            !!form.formState.errors.businessWebsite), children: [_jsx(FormTextField, { id: "customerServiceNumber", name: "customerServiceNumber", label: $t('Phone Number'), fullWidth: true, helperText: form.formState.errors.customerServiceNumber?.message }), _jsx(FormTextField, { id: "businessWebsite", name: "businessWebsite", label: $t('Business Website'), fullWidth: true, helperText: form.formState.errors.businessWebsite?.message })] }), _jsxs(ForteApplicationFormRow, { bottomSpacer: !(!!form.formState.errors.businessCategory ||
                            !!form.formState.errors.typeOfBusiness), children: [_jsxs(FormSelect, { id: "businessCategory", name: "businessCategory", label: $t('Business Category'), fullWidth: true, helperText: form.formState.errors.businessCategory?.message, onChange: async (event) => {
                                    form.setValue('businessCategory', event.target.value);
                                    form.setValue('typeOfBusiness', businessCategoriesAndTypes[event.target.value][0].Code);
                                    await form.trigger('businessCategory');
                                    await form.trigger('typeOfBusiness');
                                }, children: [!loading &&
                                        !error &&
                                        Object.keys(businessCategoriesAndTypes).map((option) => (_jsx(MenuItem, { value: option, children: option }, option))), loading && _jsx(MenuItem, { value: "", children: $t('Loading...') })] }), _jsxs(FormSelect, { id: "typeOfBusiness", name: "typeOfBusiness", helperText: form.formState.errors.typeOfBusiness?.message, label: $t('Type of Business'), fullWidth: true, children: [!loading &&
                                        businessCategoriesAndTypes[form.getValues('businessCategory')]?.map((option) => (_jsx(MenuItem, { value: option.Code, children: option.Label }, option.Code))), loading && (_jsx(MenuItem, { value: ApplicantOrganizationBusinessType.A10700, children: $t('Loading...') }))] })] }), _jsxs(ForteApplicationFormRow, { bottomSpacer: !(!!form.formState.errors.averageSaleAmount ||
                            !!form.formState.errors.maximumSaleAmount ||
                            !!form.formState.errors.annualSales), children: [_jsx(FormTextField, { id: "averageSaleAmount", name: "averageSaleAmount", label: $t('Average Reimbursement Amount'), fullWidth: true, helperText: form.formState.errors.averageSaleAmount?.message ||
                                    $t('Please estimate the average reimbursement amount for your organization') }), _jsx(FormTextField, { id: "maximumSaleAmount", name: "maximumSaleAmount", label: $t('Maximum Reimbursement Amount'), fullWidth: true, helperText: form.formState.errors.maximumSaleAmount?.message ||
                                    $t('Please estimate the largest expense report amount which will require reimbursement.') }), _jsx(FormTextField, { id: "annualSales", name: "annualSales", label: $t('Annual Reimbursement Spend'), fullWidth: true, helperText: form.formState.errors.annualSales?.message ||
                                    $t("Please estimate your organization's total reimbursement spend for the past 12 months") })] }), _jsxs(Stack, { direction: "row", justifyContent: backButtonCallback !== undefined ? 'space-between' : 'flex-end', sx: { width: '100%' }, children: [backButtonCallback !== undefined && (_jsx(Button, { variant: "outlined", onClick: backButtonCallback, children: backButtonText || $t('Back') })), _jsx(Button, { onClick: form.handleSubmit(submitForm), children: saveButtonText || $t('Next') })] })] }) }) }));
}
BusinessInformationForm.defaultProps = {
    saveButtonText: 'Next',
    afterFormSave: () => { },
    backButtonText: 'Back',
    backButtonCallback: undefined
};
export default BusinessInformationForm;
